import { useRef, useCallback, useEffect } from "react";
import { cx } from "class-variance-authority";
import { useBarControl } from "~/hooks/player/useBarControl";
import { useSeekingStore, usePlaybackStore } from "~/stores/playerStore";
import { typography } from "~/styles/typographies";
import { formatTime } from "~/utils/formatTime";
import { ProgressBar } from "./ProgressBar";
import styles from "./SeekingControl.module.css";

const CONTINUOUS_SEEKING_TIMEOUT = 50;

interface SeekingControlProps {
  onDragStart: () => void;
  onDragEnd: () => void;
  onPlay: () => void;
  onPause: () => void;
  onSeekByProgress: (nextProgress: number) => void;
}

export function SeekingControl({
  onDragStart,
  onDragEnd,
  onPlay,
  onPause,
  onSeekByProgress,
}: SeekingControlProps) {
  const pausedRef = useRef(usePlaybackStore.getState().paused);
  const prevPaused = useRef<boolean>(pausedRef.current);

  const timeout = useRef<ReturnType<typeof setTimeout>>();

  const { currentTime, currentTimeProgress, duration } = useSeekingStore(
    state => ({
      bufferedProgress: state.computed.bufferedProgress,
      currentTime: state.currentTime,
      currentTimeProgress: state.computed.currentTimeProgress,
      duration: state.duration,
    }),
  );

  useEffect(
    () =>
      usePlaybackStore.subscribe(state => {
        pausedRef.current = state.paused;
      }),
    [],
  );

  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    },
    [],
  );

  const handleDragEnd = useCallback(
    (nextProgress: number) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      onSeekByProgress(nextProgress);

      // 직전에 재생 중이었으면 자동 재생
      if (!prevPaused.current) {
        onPlay();
      }

      if (typeof onDragEnd !== "undefined") {
        onDragEnd();
      }
    },
    [onSeekByProgress, onPlay],
  );

  const handleDragStart = useCallback(() => {
    // 이전 재생 상태를 기록해둠
    prevPaused.current = pausedRef.current;
    onPause();

    if (typeof onDragStart !== "undefined") {
      onDragStart();
    }
  }, [onPause]);

  const handleDragging = useCallback(
    (nextProgress: number) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      timeout.current = setTimeout(() => {
        onSeekByProgress(nextProgress);
      }, CONTINUOUS_SEEKING_TIMEOUT);
    },
    [onSeekByProgress],
  );

  const {
    boundaryRef,
    dragPosition,
    isDragging: isBarDragging,
    handleButtonMouseDown,
    handleButtonTouchStart,
  } = useBarControl<HTMLDivElement>({
    onClick: onSeekByProgress,
    onDragEnd: handleDragEnd,
    onDragStart: handleDragStart,
    onDragging: handleDragging,
  });

  return (
    <div className={styles.seekingControl}>
      <ProgressBar
        boundaryRef={boundaryRef}
        progress={isBarDragging ? dragPosition * 100 : currentTimeProgress}
        onCircleMouseDown={handleButtonMouseDown}
        onCircleTouchStart={handleButtonTouchStart}
      />
      <div
        className={cx(
          styles.timeStamp,
          typography({ fontSize: "caption1", fontWeight: "medium" }),
        )}
      >
        <span className={styles.progress}>{formatTime(currentTime)}</span>
        <span>/</span>
        <span className={styles.duration}>{formatTime(duration)}</span>
      </div>
    </div>
  );
}
